import {
  grayColor,
  whiteColor,
  dangerColor,
  successColor,
  warningColor,
} from "assets/jss/material-dashboard-pro-react.js";
import modalStyle from "assets/jss/material-dashboard-pro-react/modalStyle";

export const STATUS_COLORS = {
  pending_validation: { bg: "#fff3e0", text: warningColor[0] || "#ff9800" },
  open: { bg: "#ffebee", text: dangerColor[0] },
  completed: { bg: "#e8f5e9", text: successColor[0] },
  archived: { bg: "#f5f5f5", text: grayColor[9] || "#ccc" },
};

const thinScrollbar = {
  scrollbarWidth: "thin",
  scrollbarColor: "transparent transparent",
  "&:hover": {
    scrollbarColor: grayColor[6] + " transparent",
  },
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "transparent",
    borderRadius: "3px",
  },
  "&:hover::-webkit-scrollbar-thumb": {
    background: grayColor[6],
  },
};

/* ─── Shared select style ─── */

const selectBase = {
  fontSize: "12px",
  padding: "5px 10px",
  border: `1px solid ${grayColor[10]}`,
  borderRadius: "4px",
  background: whiteColor,
  color: grayColor[2],
  cursor: "pointer",
  outline: "none",
  fontFamily: "inherit",
  lineHeight: "1.4",
  appearance: "auto",
  "&:focus": {
    borderColor: grayColor[6],
  },
};

const editorToolbarConfig = {
  options: ["inline", "list"],
  inline: { options: ["bold", "italic", "underline"] },
  list: { options: ["unordered", "ordered"] },
};

export { editorToolbarConfig };

/* ─── Modal ─── */

export const feedbackModalStyles = (theme) => ({
  ...modalStyle(theme),
  modal: {
    ...modalStyle(theme).modal,
    width: "86vw !important",
    maxWidth: "86vw !important",
    height: "90vh !important",
    maxHeight: "90vh !important",
    marginTop: "auto !important",
    marginBottom: "auto !important",
    transform: "none !important",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  splitContainer: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  leftPanel: {
    width: 380,
    minWidth: 320,
    maxWidth: 420,
    borderRight: `1px solid ${grayColor[10]}`,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    flexShrink: 0,
  },
  rightPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: grayColor[6],
    gap: "12px",
    padding: "40px",
    textAlign: "center",
  },
  emptyIcon: {
    fontSize: "48px",
    opacity: 0.4,
  },
  emptyText: {
    fontSize: "14px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    gap: "20px",
    paddingBottom: "50px",
    opacity: 0,
    animation: "$loadFadeIn 200ms ease-out forwards",
  },
  loadingLabel: {
    fontSize: "14px",
    color: grayColor[4],
  },
  "@keyframes loadFadeIn": {
    "0%": { opacity: 0 },
    "100%": { opacity: 1 },
  },
});

/* ─── Topic list (left panel) ─── */

export const topicListStyles = () => ({
  listHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    borderBottom: `1px solid ${grayColor[10]}`,
    flexShrink: 0,
  },
  filters: {
    display: "flex",
    gap: "6px",
    padding: "8px 14px",
    borderBottom: `1px solid ${grayColor[10]}`,
    flexShrink: 0,
    alignItems: "center",
  },
  filterBtn: {
    fontSize: "11px",
    padding: "4px 10px",
    border: `1px solid ${grayColor[10]}`,
    borderRadius: "4px",
    background: whiteColor,
    color: grayColor[2],
    cursor: "pointer",
    fontFamily: "inherit",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: grayColor[10],
    },
  },
  filterBtnActive: {
    borderColor: grayColor[6],
    fontWeight: 500,
  },
  popoverContent: {
    padding: "6px 0",
    minWidth: 180,
  },
  popoverItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "5px 14px",
    fontSize: "12px",
    color: grayColor[2],
    cursor: "pointer",
    "&:hover": {
      backgroundColor: grayColor[10],
    },
  },
  popoverCheckbox: {
    padding: 0,
    "& .MuiSvgIcon-root": {
      fontSize: "18px",
    },
  },
  topicsList: {
    flex: 1,
    overflowY: "auto",
    padding: "6px 8px",
    ...thinScrollbar,
  },
  topicCard: {
    padding: "8px 10px",
    borderRadius: "6px",
    cursor: "pointer",
    marginBottom: "2px",
    border: `1px solid transparent`,
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: `${grayColor[10]}88`,
    },
  },
  topicCardSelected: {
    border: `1px solid ${grayColor[6]}55`,
    backgroundColor: `${grayColor[10]}cc`,
  },
  topicTitle: {
    fontSize: "12px",
    fontWeight: 500,
    color: grayColor[2],
    marginBottom: "5px",
    lineHeight: "1.35",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  topicMeta: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    flexWrap: "wrap",
  },
  chip: {
    fontSize: "9px",
    fontWeight: 500,
    padding: "1px 6px",
    borderRadius: "3px",
    lineHeight: "1.5",
    whiteSpace: "nowrap",
  },
  topicFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "4px",
    fontSize: "10px",
    color: grayColor[6],
  },
  commentCount: {
    display: "flex",
    alignItems: "center",
    gap: "3px",
    fontSize: "10px",
    color: grayColor[6],
  },
  addButton: {
    fontSize: "11px",
    textTransform: "none",
    padding: "3px 10px",
    minWidth: "auto",
  },
  noResults: {
    textAlign: "center",
    padding: "30px 16px",
    color: grayColor[6],
    fontSize: "13px",
  },
});

/* ─── Topic detail (right panel) ─── */

export const topicDetailStyles = () => ({
  detailContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  detailHeader: {
    padding: "14px 20px 10px",
    borderBottom: `1px solid ${grayColor[10]}`,
    flexShrink: 0,
  },
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "8px",
  },
  titleDisplay: {
    fontSize: "15px",
    fontWeight: 500,
    color: grayColor[2],
    flex: 1,
    padding: "2px 0",
    cursor: "text",
    borderBottom: "1px solid transparent",
    lineHeight: "1.4",
    "&:hover": {
      borderBottomColor: grayColor[10],
    },
  },
  titleInput: {
    fontSize: "15px",
    fontWeight: 500,
    color: grayColor[2],
    border: "none",
    borderBottom: `1px solid ${grayColor[6]}`,
    outline: "none",
    flex: 1,
    padding: "2px 0",
    background: "transparent",
    fontFamily: "inherit",
    lineHeight: "1.4",
  },
  headerMeta: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  statusSelect: {
    ...selectBase,
    fontWeight: 400,
    fontSize: "11px",
    border: "none",
    color: whiteColor,
    padding: "3px 8px",
  },
  statusTrigger: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    fontWeight: 400,
    fontFamily: "inherit",
    padding: "3px 10px",
    borderRadius: "4px",
    cursor: "pointer",
    color: whiteColor,
    border: "none",
    lineHeight: "1.4",
  },
  statusPopoverContent: {
    padding: "4px 0",
    minWidth: 160,
  },
  statusPopoverItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 14px",
    fontSize: "11px",
    fontFamily: "inherit",
    color: grayColor[2],
    cursor: "pointer",
    "&:hover": {
      backgroundColor: grayColor[10],
    },
  },
  statusPopoverDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  statusPopoverCheck: {
    fontSize: "14px !important",
    marginLeft: "auto",
    color: grayColor[6],
  },
  categorySelect: {
    ...selectBase,
    fontWeight: 400,
    backgroundColor: grayColor[10],
    border: "none",
    padding: "3px 8px",
    fontSize: "11px",
  },
  validateBtn: {
    fontSize: "11px",
    fontWeight: 500,
    fontFamily: "inherit",
    padding: "3px 10px",
    borderRadius: "4px",
    cursor: "pointer",
    border: "none",
    color: whiteColor,
    backgroundColor: successColor[0],
    "&:disabled": { opacity: 0.4, cursor: "default" },
  },
  invalidateBtn: {
    fontSize: "11px",
    fontWeight: 500,
    fontFamily: "inherit",
    padding: "3px 10px",
    borderRadius: "4px",
    cursor: "pointer",
    border: "none",
    color: whiteColor,
    backgroundColor: dangerColor[0],
    "&:disabled": { opacity: 0.4, cursor: "default" },
  },
  detailInfo: {
    fontSize: "11px",
    color: grayColor[6],
  },
  deleteButton: {
    fontSize: "11px",
    color: grayColor[6],
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "2px",
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": {
      color: dangerColor[0],
    },
  },
  saveIndicator: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    color: grayColor[6],
    marginLeft: "auto",
    transition: "opacity 0.3s ease",
  },
  "@keyframes fadeIn": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  saveIndicatorEnter: {
    animation: "$fadeIn 0.3s ease forwards",
  },
  commentsSection: {
    flex: 1,
    overflowY: "auto",
    padding: "12px 20px",
    display: "flex",
    flexDirection: "column",
    ...thinScrollbar,
  },
  commentCard: {
    borderRadius: "6px",
    padding: "10px 12px",
    marginBottom: "8px",
    border: `1px solid ${grayColor[10]}`,
  },
  commentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "6px",
  },
  commentAuthorRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: 1,
  },
  avatar: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    fontWeight: 600,
    color: whiteColor,
    flexShrink: 0,
  },
  commentAuthor: {
    fontSize: "12px",
    fontWeight: 500,
    color: grayColor[2],
  },
  commentDate: {
    fontSize: "10px",
    color: grayColor[6],
    whiteSpace: "nowrap",
    marginLeft: "auto",
    flexShrink: 0,
  },
  commentActions: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginLeft: "8px",
    flexShrink: 0,
  },
  commentBody: {
    fontSize: "13px",
    color: grayColor[7] || "#333",
    lineHeight: "1.5",
    "& p": {
      margin: "0 0 4px",
      "&:last-child": { marginBottom: 0 },
    },
    "& strong": { fontWeight: 600 },
    "& em": { fontStyle: "italic" },
    "& ul, & ol": { paddingLeft: "20px", margin: "4px 0" },
  },
  commentEditWrapper: {
    "& .rdw-editor-wrapper": {
      border: `1px solid ${grayColor[10]}`,
      borderRadius: "4px",
    },
    "& .rdw-editor-toolbar": {
      borderBottom: `1px solid ${grayColor[10]}`,
      backgroundColor: grayColor[15],
      padding: "2px",
      marginBottom: 0,
    },
    "& .rdw-editor-main": {
      minHeight: "50px",
      maxHeight: "150px",
      overflowY: "auto",
      padding: "4px 8px",
      fontSize: "13px",
      ...thinScrollbar,
    },
  },
  commentEditActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "6px",
    marginTop: "6px",
  },
  editActionBtn: {
    fontSize: "11px",
    padding: "3px 10px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    fontWeight: 500,
    fontFamily: "inherit",
  },
  attachmentList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "5px",
    marginTop: "6px",
  },
  attachmentChip: {
    display: "flex",
    alignItems: "center",
    gap: "3px",
    fontSize: "10px",
    color: grayColor[7] || "#333",
    padding: "2px 7px",
    borderRadius: "3px",
    backgroundColor: grayColor[11] || "#f5f5f5",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: grayColor[10],
    },
  },
  actionIcon: {
    fontSize: "14px",
    cursor: "pointer",
    color: grayColor[6],
    transition: "color 0.15s",
    "&:hover": {
      color: grayColor[2],
    },
  },
  deleteIcon: {
    fontSize: "14px",
    cursor: "pointer",
    color: grayColor[6],
    transition: "color 0.15s",
    "&:hover": {
      color: dangerColor[0],
    },
  },
  confirmDeleteOverlay: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11px",
    color: dangerColor[0],
    cursor: "pointer",
  },
});

/* ─── Topic form ─── */

export const topicFormStyles = () => ({
  formContainer: {
    padding: "12px 14px",
    borderBottom: `1px solid ${grayColor[10]}`,
    backgroundColor: `${grayColor[10]}44`,
  },
  formTitle: {
    fontSize: "12px",
    fontWeight: 600,
    color: grayColor[2],
    marginBottom: "8px",
  },
  fieldGroup: {
    marginBottom: "8px",
  },
  label: {
    fontSize: "11px",
    fontWeight: 500,
    color: grayColor[6],
    marginBottom: "3px",
    display: "block",
  },
  editorWrapper: {
    "& .rdw-editor-wrapper": {
      border: `1px solid ${grayColor[10]}`,
      borderRadius: "4px",
    },
    "& .rdw-editor-toolbar": {
      borderBottom: `1px solid ${grayColor[10]}`,
      backgroundColor: grayColor[15],
      padding: "2px",
      marginBottom: 0,
    },
    "& .rdw-editor-main": {
      minHeight: "106px",
      maxHeight: "160px",
      overflowY: "auto",
      padding: "4px 8px",
      fontSize: "12px",
      ...thinScrollbar,
    },
  },
  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "6px",
  },
  formBtn: {
    fontSize: "11px",
    padding: "4px 14px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    fontWeight: 500,
    fontFamily: "inherit",
  },
  cancelBtn: {
    background: grayColor[10],
    color: grayColor[3],
    "&:hover": {
      background: grayColor[9] || "#e0e0e0",
    },
  },
  submitBtn: {
    background: grayColor[2],
    color: whiteColor,
    "&:hover": {
      opacity: 0.85,
    },
    "&:disabled": {
      opacity: 0.4,
      cursor: "default",
    },
  },
});

/* ─── Comment form ─── */

export const commentFormStyles = () => ({
  formContainer: {
    padding: "10px 20px 12px",
    borderTop: `1px solid ${grayColor[10]}`,
    flexShrink: 0,
  },
  editorWrapper: {
    marginBottom: "8px",
    "& .rdw-editor-wrapper": {
      border: `1px solid ${grayColor[10]}`,
      borderRadius: "4px",
    },
    "& .rdw-editor-toolbar": {
      borderBottom: `1px solid ${grayColor[10]}`,
      backgroundColor: grayColor[15],
      padding: "2px",
      marginBottom: 0,
    },
    "& .rdw-editor-main": {
      minHeight: "106px",
      maxHeight: "155px",
      overflowY: "auto",
      padding: "4px 10px",
      fontSize: "13px",
      ...thinScrollbar,
    },
  },
  formFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  attachSection: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flex: 1,
    flexWrap: "wrap",
  },
  attachBtn: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    color: grayColor[3],
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "4px",
    border: `1px solid ${grayColor[10]}`,
    background: "transparent",
    fontFamily: "inherit",
    "&:hover": {
      backgroundColor: grayColor[10],
    },
  },
  attachedFile: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "10px",
    color: grayColor[3],
    padding: "2px 7px",
    borderRadius: "4px",
    backgroundColor: grayColor[11] || "#f5f5f5",
  },
  removeAttach: {
    fontSize: "13px",
    cursor: "pointer",
    color: grayColor[6],
    "&:hover": {
      color: dangerColor[0],
    },
  },
  sendBtn: {
    fontSize: "11px",
    padding: "5px 14px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    fontWeight: 500,
    fontFamily: "inherit",
    color: whiteColor,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: "6px",
    "&:disabled": {
      opacity: 0.4,
      cursor: "default",
    },
  },
});
